// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * PDF assembly, in the tab.
 *
 * pdf-lib is pure JavaScript, so the finished file is genuinely built on the
 * user's own machine — there is no service, and no WebAssembly blob fetched
 * from anywhere, involved in producing the bytes they download.
 */

import { PDFDocument } from 'pdf-lib';
import type { PaperSize, ScanPage } from './types';

/** Points, at 72 per inch. */
const PAPER: Record<Exclude<PaperSize, 'auto'>, { width: number; height: number }> = {
  a4: { width: 595.28, height: 841.89 },
  letter: { width: 612, height: 792 },
};

/** Treating a scan as 150 dpi gives a sane physical size for "fit to page shape". */
const ASSUMED_DPI = 150;

export interface Placement {
  pageWidth: number;
  pageHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Works out the PDF page size and where the image sits on it.
 *
 * For a fixed paper size the sheet is rotated to match the image's orientation
 * — a landscape receipt on a portrait A4 would otherwise waste half the page —
 * and the image is scaled to fit inside a small margin and centred.
 */
export function layoutPage(
  imageWidth: number,
  imageHeight: number,
  paperSize: PaperSize,
  marginPt = 18,
): Placement {
  if (imageWidth <= 0 || imageHeight <= 0) {
    throw new Error('Page has no dimensions');
  }

  if (paperSize === 'auto') {
    const pageWidth = (imageWidth / ASSUMED_DPI) * 72;
    const pageHeight = (imageHeight / ASSUMED_DPI) * 72;
    return { pageWidth, pageHeight, x: 0, y: 0, width: pageWidth, height: pageHeight };
  }

  const sheet = PAPER[paperSize];
  const landscape = imageWidth > imageHeight;
  const pageWidth = landscape ? sheet.height : sheet.width;
  const pageHeight = landscape ? sheet.width : sheet.height;

  const available = {
    width: Math.max(1, pageWidth - marginPt * 2),
    height: Math.max(1, pageHeight - marginPt * 2),
  };
  const scale = Math.min(available.width / imageWidth, available.height / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;

  return {
    pageWidth,
    pageHeight,
    x: (pageWidth - width) / 2,
    y: (pageHeight - height) / 2,
    width,
    height,
  };
}

/** Assembles the finished pages into a single PDF. */
export async function buildPdf(pages: readonly ScanPage[], paperSize: PaperSize): Promise<Blob> {
  if (pages.length === 0) throw new Error('There are no pages to export.');

  // `updateMetadata: false` stops pdf-lib rewriting the info dictionary on save.
  // Without it our own producer string is discarded — and, more to the point,
  // pdf-lib stamps the exact creation and modification time into the file, which
  // quietly records when the document was scanned.
  const doc = await PDFDocument.create({ updateMetadata: false });
  doc.setTitle('Scan');
  doc.setProducer('Papershot');
  doc.setCreator('Papershot — papershot.benrichardson.dev');

  for (const page of pages) {
    const bytes = new Uint8Array(await page.blob.arrayBuffer());
    const image =
      page.format === 'png' ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
    const layout = layoutPage(page.width, page.height, paperSize);

    const pdfPage = doc.addPage([layout.pageWidth, layout.pageHeight]);
    pdfPage.drawImage(image, {
      x: layout.x,
      y: layout.y,
      width: layout.width,
      height: layout.height,
    });
  }

  const bytes = await doc.save();
  // A fresh copy so the Blob owns a plain ArrayBuffer, not a view into pdf-lib's.
  return new Blob([bytes.slice()], { type: 'application/pdf' });
}
